import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../../UI';
import serverAnalyticsService from '../../../services/ServerAnalyticsService';
import '../../../styles/DatabaseModal.css';
import { FaAws, FaDatabase, FaServer, FaTable, FaChartBar, FaSearch, FaTimes, FaSync, FaExternalLinkAlt, FaBook, FaCopy } from 'react-icons/fa';
import mongoDBAnalyticsService from '../../../services/MongoDBAnalyticsService';
import s3AnalyticsService from '../../../services/S3AnalyticsService';
import postgreSQLAnalyticsService from '../../../services/PostgreSQLAnalyticsService';
import { LineChart, BarChart, PieChart } from '../../Charts';

// Import your chart components or use placeholders if they don't exist

const DatabaseModal = ({ database, data, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [queryInput, setQueryInput] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [refreshTime, setRefreshTime] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const [modalData, setModalData] = useState(data);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDatabase, setSelectedDatabase] = useState('causeandimpact');
  const [expandedRows, setExpandedRows] = useState({});
  
  // Set default query examples based on database type
  useEffect(() => {
    switch (database) {
      case 'mongodb':
        setQueryInput('db.users.find({}).limit(5)');
        break;
      case 'postgresql':
        setQueryInput('SELECT * FROM users LIMIT 5;');
        break;
      case 's3':
        setQueryInput('list-objects --bucket your-bucket-name');
        break;
      case 'api':
        setQueryInput('GET /api/health');
        break;
      default:
        setQueryInput('');
    }
  }, [database]);
  
  // Auto-refresh data on monitor tab
  useEffect(() => {
    if (activeTab === 'monitor') {
      const interval = setInterval(() => {
        setRefreshTime(Date.now());
      }, 30000); // Refresh every 30 seconds
      setRefreshInterval(interval);
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [activeTab]);
  
  useEffect(() => {
    // Fetch data when modal opens based on database type
    if (database === 's3') {
      fetchS3Data();
    } else if (database === 'mongodb') {
      fetchDatabaseData();
    } else if (database === 'postgresql') {
      fetchPostgreSQLData();
    }
    // ... other database types
  }, [database]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
  };
  
  const executeQuery = async () => {
    if (!queryInput.trim()) return;
    
    setLoading(true);
    setError(null);
    setQueryResult(null);
    
    try {
      let result;
      
      switch (database) {
        case 'mongodb':
          result = await serverAnalyticsService.executeMongoQuery(queryInput);
          break;
        case 'postgresql':
          result = await serverAnalyticsService.executeSqlQuery(queryInput);
          break;
        case 's3':
          result = await serverAnalyticsService.executeS3Command(queryInput);
          break;
        case 'api':
          result = await serverAnalyticsService.testApiEndpoint(queryInput);
          break;
        default:
          throw new Error('Unsupported database type');
      }
      
      setQueryResult(result);
    } catch (err) {
      console.error('Query execution error:', err);
      setError(err.message || 'An error occurred while executing the query');
    } finally {
      setLoading(false);
    }
  };
  
  const getDatabaseTitle = () => {
    switch (database) {
      case 's3':
        return 'AWS S3 Storage';
      case 'mongodb':
        return 'MongoDB Database';
      case 'postgresql':
        return 'PostgreSQL Database';
      case 'api':
        return 'API Server';
      default:
        return 'Database';
    }
  };
  
  const getStatusClass = () => {
    if (data?.error) return 'error';
    return data?.status === 'online' || data?.status === 'healthy' ? 'healthy' : 'error';
  };

  const renderDatabaseContent = () => {
    switch (database) {
      case 's3':
        return renderS3Content();
      case 'mongodb':
        return renderMongoDBContent();
      case 'postgresql':
        return renderPostgreSQLContent();
      case 'api':
        return renderApiContent();
      default:
        return <div>Unsupported database type</div>;
    }
  };
  
  const renderS3Content = () => {
    // Use modalData instead of data if available
    const s3Data = modalData || data;
    
    return (
      <div className="modal-content-container">
        {activeTab === 'overview' && (
          <div className="db-overview">
            <div className="stat-grid">
              <div className="stat-card">
                <h4>Total Buckets</h4>
                <div className="stat-value">{s3Data?.buckets?.length || 0}</div>
              </div>
              <div className="stat-card">
                <h4>Total Size</h4>
                <div className="stat-value">{s3Data?.totalSize || '0 MB'}</div>
              </div>
              <div className="stat-card">
                <h4>Total Objects</h4>
                <div className="stat-value">{s3Data?.totalObjects || 0}</div>
              </div>
              <div className="stat-card">
                <h4>Connection Status</h4>
                <div className={`stat-value ${s3Data?.connectionState?.status === 'connected' ? 'connected' : 'disconnected'}`}>
                  {s3Data?.connectionState?.status === 'connected' ? 'Connected' : 'Disconnected'}
                </div>
              </div>
            </div>
            
            <div className="bucket-list">
              <h4>Buckets</h4>
              {s3Data?.buckets?.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Created</th>
                      <th>Size</th>
                      <th>Objects</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s3Data.buckets.map((bucket, index) => (
                      <tr key={index}>
                        <td>{bucket.name}</td>
                        <td>{new Date(bucket.created).toLocaleDateString()}</td>
                        <td>{bucket.sizeFormatted || '0 MB'}</td>
                        <td>{bucket.objectCount || 0}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="action-button"
                              onClick={() => setQueryInput(`list-objects --bucket ${bucket.name}`)}
                              title="List Objects"
                            >
                              📋
                            </button>
                            <button 
                              className="action-button"
                              onClick={() => window.open(`https://s3.console.aws.amazon.com/s3/buckets/${bucket.name}`, '_blank')}
                              title="Open in AWS Console"
                            >
                              🔗
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  {loading ? <LoadingSpinner size="small" /> : 'No buckets found'}
                </div>
              )}
            </div>
            
            {s3Data?.connectionState?.error && (
              <div className="error-panel">
                <h4>Connection Error</h4>
                <div className="error-message">
                  {s3Data.connectionState.error}
                </div>
                {s3Data.connectionState.diagnostics && (
                  <div className="diagnostics">
                    <h5>Diagnostics</h5>
                    <pre>{JSON.stringify(s3Data.connectionState.diagnostics, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'query' && (
          <div className="query-interface">
            <div className="query-header">
              <h4>S3 Command</h4>
              <div className="query-help">
                Example: list-objects --bucket my-bucket
              </div>
            </div>
            <div className="query-input-container">
              <textarea 
                className="query-input"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                placeholder="Enter S3 CLI command..."
                rows={3}
              />
              <button 
                className="execute-button"
                onClick={executeQuery}
                disabled={loading}
              >
                {loading ? 'Executing...' : 'Execute'}
              </button>
            </div>
            
            {error && (
              <div className="query-error">
                {error}
              </div>
            )}
            
            {loading && (
              <div className="query-loading">
                <LoadingSpinner size="small" />
                <span>Executing command...</span>
              </div>
            )}
            
            {queryResult && (
              <div className="query-result">
                <div className="result-header">
                  <h4>Result</h4>
                  <button 
                    className="copy-button"
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(queryResult, null, 2))}
                  >
                    Copy
                  </button>
                </div>
                <pre className="result-content">{JSON.stringify(queryResult, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMongoDBContent = () => {
    if (loading) {
      return <LoadingSpinner message="Loading MongoDB data..." />;
    }

    if (error) {
      return (
        <div className="error-message">
          <p>Error loading MongoDB data: {error}</p>
          <button onClick={handleRefresh} className="refresh-button">
            <FaSync /> Retry
          </button>
        </div>
      );
    }

    if (!modalData || !modalData.clusters || modalData.clusters.length === 0) {
      return (
        <div className="no-data-message">
          <p>No MongoDB data available.</p>
          <button onClick={handleRefresh} className="refresh-button">
            <FaSync /> Refresh
          </button>
        </div>
      );
    }

    const cluster = modalData.clusters[0];
    
    switch (activeTab) {
      case 'overview':
        return (
          <div className="database-overview">
            <div className="stats-grid">
              <div className="stat-card">
                <h4>Connection Status</h4>
                <div className="status-badge connected">
                  Connected
                </div>
                <p className="last-checked">
                  Last checked: {new Date().toLocaleTimeString()}
                </p>
              </div>
              
              <div className="stat-card">
                <h4>Database Info</h4>
                <p><strong>Version:</strong> MongoDB 5.0</p>
                <p><strong>Host:</strong> causeandimpact.mongodb.net</p>
                <p><strong>Database:</strong> causeandimpact</p>
              </div>
              
              <div className="stat-card">
                <h4>Storage</h4>
                <p><strong>Size:</strong> 36KB</p>
                <p><strong>Disk Size:</strong> 512MB</p>
                <div className="progress-bar">
                  <div 
                    className="progress-value" 
                    style={{ width: `${(36 / 512000) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="stat-card">
                <h4>Collections</h4>
                <p><strong>Count:</strong> 13</p>
                <p><strong>Documents:</strong> 356</p>
                <p><strong>Indexes:</strong> 35</p>
              </div>
            </div>
            
            <div className="data-tables-section">
              <h4>Database Summary</h4>
              <div className="table-container">
                <table className="data-table expandable-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Value</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      className={expandedRows['databases'] ? 'expanded' : ''} 
                      onClick={() => toggleRowExpansion('databases')}
                    >
                      <td>Total Databases</td>
                      <td>1</td>
                      <td><span className="status-badge normal">Normal</span></td>
                    </tr>
                    {expandedRows['databases'] && (
                      <tr className="expansion-row">
                        <td colSpan="3">
                          <div className="expansion-content">
                            <h5>Database Details</h5>
                            <table className="nested-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Collections</th>
                                  <th>Size</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>causeandimpact</td>
                                  <td>13</td>
                                  <td>36KB</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    <tr 
                      className={expandedRows['collections'] ? 'expanded' : ''} 
                      onClick={() => toggleRowExpansion('collections')}
                    >
                      <td>Total Collections</td>
                      <td>13</td>
                      <td><span className="status-badge normal">Normal</span></td>
                    </tr>
                    {expandedRows['collections'] && (
                      <tr className="expansion-row">
                        <td colSpan="3">
                          <div className="expansion-content">
                            <h5>Collections</h5>
                            <div className="collections-grid">
                              {['activities', 'analytics', 'applications', 'budgets', 'causes', 
                                'documents', 'donations', 'fiscalsponsors', 'organizations', 
                                'receipts', 'transactions', 'users', 'waitlists'].map(collection => (
                                <div key={collection} className="collection-item">
                                  <span className="collection-icon"><FaDatabase size={12} /></span>
                                  <span className="collection-name">{collection}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    <tr 
                      className={expandedRows['documents'] ? 'expanded' : ''} 
                      onClick={() => toggleRowExpansion('documents')}
                    >
                      <td>Total Documents</td>
                      <td>356</td>
                      <td><span className="status-badge normal">Normal</span></td>
                    </tr>
                    {expandedRows['documents'] && (
                      <tr className="expansion-row">
                        <td colSpan="3">
                          <div className="expansion-content">
                            <h5>Documents by Collection</h5>
                            <table className="nested-table">
                              <thead>
                                <tr>
                                  <th>Collection</th>
                                  <th>Documents</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr><td>activities</td><td>24</td></tr>
                                <tr><td>analytics</td><td>18</td></tr>
                                <tr><td>applications</td><td>32</td></tr>
                                <tr><td>budgets</td><td>15</td></tr>
                                <tr><td>causes</td><td>4</td></tr>
                                <tr><td>documents</td><td>27</td></tr>
                                <tr><td>donations</td><td>48</td></tr>
                                <tr><td>fiscalsponsors</td><td>12</td></tr>
                                <tr><td>organizations</td><td>29</td></tr>
                                <tr><td>receipts</td><td>36</td></tr>
                                <tr><td>transactions</td><td>52</td></tr>
                                <tr><td>users</td><td>41</td></tr>
                                <tr><td>waitlists</td><td>18</td></tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    <tr>
                      <td>Storage Size</td>
                      <td>36KB</td>
                      <td><span className="status-badge normal">Normal</span></td>
                    </tr>
                    
                    <tr>
                      <td>Active Connections</td>
                      <td>3</td>
                      <td><span className="status-badge normal">Normal</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <h4>Connection Information</h4>
              <div className="connection-info-card">
                <div className="connection-info-grid">
                  <div className="connection-info-item">
                    <div className="info-label">Connection String:</div>
                    <div className="info-value connection-string">
                      mongodb+srv://causeandimpact.mongodb.net/causeandimpact
                      <button className="copy-button" onClick={() => copyToClipboard("mongodb+srv://causeandimpact.mongodb.net/causeandimpact")}>
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                  
                  <div className="connection-info-item">
                    <div className="info-label">Host:</div>
                    <div className="info-value">causeandimpact.mongodb.net</div>
                  </div>
                  
                  <div className="connection-info-item">
                    <div className="info-label">Port:</div>
                    <div className="info-value">27017</div>
                  </div>
                  
                  <div className="connection-info-item">
                    <div className="info-label">Version:</div>
                    <div className="info-value">MongoDB 5.0</div>
                  </div>
                  
                  <div className="connection-info-item">
                    <div className="info-label">Authentication:</div>
                    <div className="info-value">Enabled</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'collections':
        return (
          <div className="collections-tab">
            <div className="search-bar">
              <FaSearch />
              <input 
                type="text" 
                placeholder="Search collections..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="database-selector">
              <span>Database:</span>
              <select 
                value={selectedDatabase} 
                onChange={(e) => setSelectedDatabase(e.target.value)}
              >
                <option value="causeandimpact">causeandimpact</option>
                {/* Add other databases if available */}
              </select>
              <span className="database-stats">
                Collections: {modalData.collectionCount || 13} | Storage Size: {modalData.storageSize ? formatBytes(modalData.storageSize) : '36KB'}
              </span>
            </div>
            
            <div className="collections-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Collection Name</th>
                    <th>Documents</th>
                    <th>Size</th>
                    <th>Avg Doc Size</th>
                    <th>Indexes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Replace with your actual collections */}
                  <tr>
                    <td>activities</td>
                    <td>24</td>
                    <td>4.2KB</td>
                    <td>178B</td>
                    <td>2</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>analytics</td>
                    <td>18</td>
                    <td>3.8KB</td>
                    <td>211B</td>
                    <td>2</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>applications</td>
                    <td>32</td>
                    <td>7.6KB</td>
                    <td>237B</td>
                    <td>3</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>budgets</td>
                    <td>15</td>
                    <td>4.1KB</td>
                    <td>273B</td>
                    <td>2</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>causes</td>
                    <td>4</td>
                    <td>2.8KB</td>
                    <td>700B</td>
                    <td>3</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>documents</td>
                    <td>27</td>
                    <td>5.2KB</td>
                    <td>192B</td>
                    <td>2</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>donations</td>
                    <td>48</td>
                    <td>9.6KB</td>
                    <td>200B</td>
                    <td>4</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>fiscalsponsors</td>
                    <td>12</td>
                    <td>3.4KB</td>
                    <td>283B</td>
                    <td>2</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>organizations</td>
                    <td>29</td>
                    <td>6.8KB</td>
                    <td>234B</td>
                    <td>3</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>receipts</td>
                    <td>36</td>
                    <td>7.2KB</td>
                    <td>200B</td>
                    <td>3</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>transactions</td>
                    <td>52</td>
                    <td>10.4KB</td>
                    <td>200B</td>
                    <td>4</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>users</td>
                    <td>41</td>
                    <td>8.2KB</td>
                    <td>200B</td>
                    <td>3</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                  <tr>
                    <td>waitlists</td>
                    <td>18</td>
                    <td>3.6KB</td>
                    <td>200B</td>
                    <td>2</td>
                    <td>
                      <button className="action-button small">View</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="pagination">
              <span>Showing 1-13 of 13</span>
              <div className="pagination-controls">
                <button disabled>Previous</button>
                <button disabled>Next</button>
              </div>
            </div>
          </div>
        );
        
      case 'performance':
        return (
          <div className="performance-tab">
            <div className="performance-metrics">
              <div className="metric-card">
                <h4>CPU Usage</h4>
                <div className="gauge-chart">
                  <div className="gauge-value" style={{ transform: `rotate(${(modalData.performance?.cpu?.user || 0) * 1.8}deg)` }}></div>
                  <div className="gauge-label">{Math.round(modalData.performance?.cpu?.user || 0)}%</div>
                </div>
              </div>
              
              <div className="metric-card">
                <h4>Memory Usage</h4>
                <div className="gauge-chart">
                  <div className="gauge-value" style={{ transform: `rotate(${(modalData.performance?.memory?.resident || 0) / 10 * 1.8}deg)` }}></div>
                  <div className="gauge-label">{mongoDBAnalyticsService.formatBytes(modalData.performance?.memory?.resident || 0)}</div>
                </div>
              </div>
              
              <div className="metric-card">
                <h4>Network I/O</h4>
                <p><strong>Bytes In:</strong> {mongoDBAnalyticsService.formatBytes(modalData.performance?.network?.bytesIn || 0)}/s</p>
                <p><strong>Bytes Out:</strong> {mongoDBAnalyticsService.formatBytes(modalData.performance?.network?.bytesOut || 0)}/s</p>
                <p><strong>Connections:</strong> {modalData.health?.connections?.current || 0}</p>
              </div>
              
              <div className="metric-card">
                <h4>Disk I/O</h4>
                <p><strong>Reads:</strong> {modalData.performance?.diskIO?.reads || 0}</p>
                <p><strong>Writes:</strong> {modalData.performance?.diskIO?.writes || 0}</p>
              </div>
            </div>
            
            <div className="performance-chart">
              <h4>Operations Per Second</h4>
              <LineChart 
                data={[
                  { name: 'Queries', value: modalData.performance?.operations?.queries || 0 },
                  { name: 'Inserts', value: modalData.performance?.operations?.inserts || 0 },
                  { name: 'Updates', value: modalData.performance?.operations?.updates || 0 },
                  { name: 'Deletes', value: modalData.performance?.operations?.deletes || 0 }
                ]}
                xKey="name"
                yKey="value"
                height={250}
              />
            </div>
          </div>
        );
        
      default:
        return <div>Select a tab to view MongoDB data</div>;
    }
  };

  const renderPostgreSQLContent = () => {
    // Use modalData instead of data
    const postgresData = modalData || {};
    console.log("Rendering PostgreSQL content with data:", postgresData);
    
    // Get environment variables
    const host = process.env.REACT_APP_POSTGRES_HOST;
    const database = process.env.REACT_APP_POSTGRES_DATABASE;
    const port = process.env.REACT_APP_POSTGRES_PORT;
    const ssl = process.env.REACT_APP_POSTGRES_SSL;
    
    // Extract database information
    const databasesList = postgresData.databases || [];
    const totalDatabases = databasesList.length || 0;
    const totalSize = postgresData.totalSize || '0 MB';
    const activeConnections = postgresData.activeConnections || 0;
    const version = postgresData.version || 'PostgreSQL 16.3';
    
    return (
      <div className="postgresql-modal-content">
        <div className="modal-section overview-section">
          <h3>PostgreSQL Database Overview</h3>
          <div className="database-stats-grid">
            <div className="stat-card">
              <h4>Version</h4>
              <div className="stat-value">{version}</div>
            </div>
            <div className="stat-card">
              <h4>Total Databases</h4>
              <div className="stat-value">{totalDatabases}</div>
            </div>
            <div className="stat-card">
              <h4>Total Size</h4>
              <div className="stat-value">{totalSize}</div>
            </div>
            <div className="stat-card">
              <h4>Active Connections</h4>
              <div className="stat-value">{activeConnections}</div>
            </div>
          </div>
        </div>
        
        <div className="modal-section database-details-section">
          <h3>Database Details</h3>
          <table className="database-stats-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
                <th>Owner</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {databasesList.length > 0 ? (
                databasesList.map((db, index) => (
                  <tr key={index}>
                    <td>{db.name}</td>
                    <td>{db.size || 'N/A'}</td>
                    <td>{db.owner || 'postgres'}</td>
                    <td>
                      <span className={`status-indicator status-${db.isHealthy !== false ? 'healthy' : 'unhealthy'}`}>
                        {db.isHealthy !== false ? 'Healthy' : 'Unhealthy'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center' }}>
                    {database ? database : 'No databases found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="modal-section performance-section">
          <h3>Database Performance</h3>
          <div className="performance-metrics">
            <div className="performance-metric">
              <div className="metric-header">
                <span className="metric-label">CPU Usage</span>
                <span className="metric-value">{postgresData?.cpuUsage || '25'}%</span>
              </div>
              <div className="meter-container">
                <div 
                  className={`meter-fill ${
                    (postgresData?.cpuUsage || 25) > 80 ? 'critical' : 
                    (postgresData?.cpuUsage || 25) > 60 ? 'high' : 
                    (postgresData?.cpuUsage || 25) > 40 ? 'medium' : 'low'
                  }`} 
                  style={{ width: `${postgresData?.cpuUsage || 25}%` }}
                ></div>
                <div className="meter-markers">
                  <div className="marker" style={{ left: '25%' }}></div>
                  <div className="marker" style={{ left: '50%' }}></div>
                  <div className="marker" style={{ left: '75%' }}></div>
                </div>
              </div>
            </div>
            
            <div className="performance-metric">
              <div className="metric-header">
                <span className="metric-label">Memory Usage</span>
                <span className="metric-value">{postgresData?.memoryUsage || '40'}%</span>
              </div>
              <div className="meter-container">
                <div 
                  className={`meter-fill ${
                    (postgresData?.memoryUsage || 40) > 80 ? 'critical' : 
                    (postgresData?.memoryUsage || 40) > 60 ? 'high' : 
                    (postgresData?.memoryUsage || 40) > 40 ? 'medium' : 'low'
                  }`} 
                  style={{ width: `${postgresData?.memoryUsage || 40}%` }}
                ></div>
                <div className="meter-markers">
                  <div className="marker" style={{ left: '25%' }}></div>
                  <div className="marker" style={{ left: '50%' }}></div>
                  <div className="marker" style={{ left: '75%' }}></div>
                </div>
              </div>
            </div>
            
            <div className="performance-metric">
              <div className="metric-header">
                <span className="metric-label">Disk I/O</span>
                <span className="metric-value">{postgresData?.diskIO || '15'}%</span>
              </div>
              <div className="meter-container">
                <div 
                  className={`meter-fill ${
                    (postgresData?.diskIO || 15) > 80 ? 'critical' : 
                    (postgresData?.diskIO || 15) > 60 ? 'high' : 
                    (postgresData?.diskIO || 15) > 40 ? 'medium' : 'low'
                  }`} 
                  style={{ width: `${postgresData?.diskIO || 15}%` }}
                ></div>
                <div className="meter-markers">
                  <div className="marker" style={{ left: '25%' }}></div>
                  <div className="marker" style={{ left: '50%' }}></div>
                  <div className="marker" style={{ left: '75%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-section">
          <h3>Connection Information</h3>
          <div className="connection-info-grid">
            <div className="info-item">
              <div className="info-label">Host:</div>
              <div className="info-value">
                {host || postgresData.host || 'Not available'}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Database:</div>
              <div className="info-value">
                {database || postgresData.databases?.[0]?.name || 'Not available'}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Port:</div>
              <div className="info-value">
                {port || postgresData.port || '5432'}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">SSL:</div>
              <div className="info-value">
                {ssl === 'true' ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-actions">
          <a 
            href="https://console.aws.amazon.com/rds/home" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="action-button aws-button"
          >
            <FaExternalLinkAlt className="button-icon" /> 
            <span className="button-text">AWS RDS CONSOLE</span>
          </a>
          <a 
            href="https://www.postgresql.org/docs/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="action-button docs-button"
          >
            <FaBook className="button-icon" /> 
            <span className="button-text">POSTGRESQL DOCS</span>
          </a>
        </div>
      </div>
    );
  };

  const renderApiContent = () => {
    return (
      <div className="modal-content-container">
        {activeTab === 'overview' && (
          <div className="db-overview">
            <div className="stat-grid">
              <div className="stat-card">
                <h4>Uptime</h4>
                <div className="stat-value">99.8%</div>
              </div>
              <div className="stat-card">
                <h4>Response Time</h4>
                <div className="stat-value">124ms</div>
              </div>
              <div className="stat-card">
                <h4>Requests/min</h4>
                <div className="stat-value">342</div>
              </div>
              <div className="stat-card">
                <h4>Error Rate</h4>
                <div className="stat-value">0.2%</div>
              </div>
            </div>
            
            <div className="endpoint-list">
              <h4>API Endpoints</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Path</th>
                    <th>Method</th>
                    <th>Response Time</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>/api/users</td>
                    <td>GET</td>
                    <td>118ms</td>
                    <td>Healthy</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-button"
                          onClick={() => setQueryInput('GET /api/users')}
                          title="Test Endpoint"
                        >
                          🔍
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>/api/projects</td>
                    <td>GET</td>
                    <td>142ms</td>
                    <td>Healthy</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-button"
                          onClick={() => setQueryInput('GET /api/projects')}
                          title="Test Endpoint"
                        >
                          🔍
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>/api/auth</td>
                    <td>POST</td>
                    <td>95ms</td>
                    <td>Healthy</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-button"
                          onClick={() => setQueryInput('POST /api/auth')}
                          title="Test Endpoint"
                        >
                          🔍
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'query' && (
          <div className="query-interface">
            <div className="query-header">
              <h4>API Test</h4>
              <div className="query-help">
                Example: GET /api/users
              </div>
            </div>
            <div className="query-input-container">
              <textarea 
                className="query-input"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                placeholder="Enter API request (METHOD /path)..."
                rows={3}
              />
              <button 
                className="execute-button"
                onClick={executeQuery}
                disabled={loading}
              >
                {loading ? 'Executing...' : 'Execute'}
              </button>
            </div>
            
            {error && (
              <div className="query-error">
                {error}
              </div>
            )}
            
            {loading && (
              <div className="query-loading">
                <LoadingSpinner size="small" />
                <span>Executing request...</span>
              </div>
            )}
            
            {queryResult && (
              <div className="query-result">
                <div className="result-header">
                  <h4>Result</h4>
                  <button 
                    className="copy-button"
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(queryResult, null, 2))}
                  >
                    Copy
                  </button>
                </div>
                <pre className="result-content">{JSON.stringify(queryResult, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'monitor' && (
          <div className="monitoring-panel">
            <div className="monitor-header">
              <h4>API Metrics</h4>
              <div className="last-updated">
                Last updated: {new Date(refreshTime).toLocaleTimeString()}
              </div>
            </div>
            
            <div className="metric-charts">
              <div className="chart-container">
                <h5>Response Time</h5>
                <div className="chart-wrapper">
                  <div className="chart-placeholder">
                    <div className="chart-line"></div>
                    <div className="chart-labels">
                      <span>24h ago</span>
                      <span>Now</span>
                    </div>
                  </div>
                  <div className="chart-legend">
                    <div className="legend-item">
                      <div className="legend-color" style={{backgroundColor: '#10B981'}}></div>
                      <div className="legend-label">Avg: 124ms</div>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color" style={{backgroundColor: '#F59E0B'}}></div>
                      <div className="legend-label">95th Percentile: 312ms</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="chart-container">
                <h5>Request Volume</h5>
                <div className="chart-wrapper">
                  <div className="chart-placeholder">
                    <div className="chart-area"></div>
                  </div>
                  <div className="chart-legend">
                    <div className="legend-item">
                      <div className="legend-color" style={{backgroundColor: '#60A5FA'}}></div>
                      <div className="legend-label">Total: 24,532 requests today</div>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color" style={{backgroundColor: '#EF4444'}}></div>
                      <div className="legend-label">Errors: 49 (0.2%)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="insights-panel">
              <h5>API Insights</h5>
              <div className="insight-item">
                <div className="insight-icon warning">⚠️</div>
                <div className="insight-content">
                  <div className="insight-title">Slow Endpoint</div>
                  <div className="insight-message">
                    The /api/projects endpoint is 35% slower than average. Consider optimizing the database query.
                  </div>
                </div>
                <button className="insight-action">Analyze</button>
              </div>
              <div className="insight-item">
                <div className="insight-icon info">ℹ️</div>
                <div className="insight-content">
                  <div className="insight-title">Rate Limiting</div>
                  <div className="insight-message">
                    IP 203.0.113.42 has made 1,200+ requests in the last hour. Consider implementing rate limiting.
                  </div>
                </div>
                <button className="insight-action">Block IP</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (database === 'mongodb') {
      await fetchDatabaseData();
    } else if (database === 's3') {
      await fetchS3Data();
    } else if (database === 'postgresql') {
      await fetchPostgreSQLData();
    }
    setRefreshing(false);
  };

  const fetchDatabaseData = async () => {
    if (database !== 'mongodb') return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching MongoDB data for modal...');
      const mongoData = await mongoDBAnalyticsService.getMongoDBAnalytics();
      console.log('MongoDB data received:', mongoData);
      
      setModalData(mongoData);
    } catch (err) {
      console.error('Error fetching MongoDB data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchS3Data = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching S3 data for modal...');
      const s3Data = await s3AnalyticsService.getS3Analytics();
      console.log('S3 data received:', s3Data);
      
      setModalData(s3Data);
    } catch (err) {
      console.error('Error fetching S3 data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPostgreSQLData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching PostgreSQL data for modal...');
      const postgresData = await postgreSQLAnalyticsService.getPostgreSQLAnalytics();
      console.log('PostgreSQL data received:', postgresData);
      
      setModalData(postgresData);
    } catch (err) {
      console.error('Error fetching PostgreSQL data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        // You could add a toast notification here
        console.log('Copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  return (
    <div className="database-modal" onClick={onClose}>
      <div className="database-modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="database-icon-large">
            {database === 's3' && <FaAws />}
            {database === 'mongodb' && <FaDatabase />}
            {database === 'postgresql' && <FaDatabase />}
            {database === 'api' && <FaServer />}
          </div>
          <h2>
            {database === 's3' && 'S3 Storage'}
            {database === 'mongodb' && 'MongoDB'}
            {database === 'postgresql' && 'PostgreSQL'}
            {database === 'api' && 'API Server'}
          </h2>
          <div className={`status-badge ${modalData?.status === 'connected' || modalData?.connectionState?.status === 'connected' ? 'online' : 'offline'}`}>
            {modalData?.status === 'connected' || modalData?.connectionState?.status === 'connected' ? 'Connected' : 'Disconnected'}
          </div>
          <button className="refresh-button" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="modal-close-button" onClick={onClose}>×</button>
        </div>
        
        {loading ? (
          <div className="loading-container">
            <LoadingSpinner />
            <p>Loading database information...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">Error: {error}</p>
            <button className="retry-button" onClick={handleRefresh}>
              Retry
            </button>
          </div>
        ) : (
          renderDatabaseContent()
        )}
      </div>
    </div>
  );
};

export default DatabaseModal; 